import { Circle } from '@mui/icons-material';
import {
  Box,
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
} from '@mui/material';
import {
  useDeleteEmailTemplateMutation,
  useListEmailTemplateRelatedItemsQuery,
} from 'api/client-domain/email-template-service/email-template-service';
import ConfirmationDrawer from 'components/drawers/confirmation-drawer/confirmation-drawer.component';
import { IncDrawerType } from 'components/drawers/inc-drawer/inc-drawer.component';
import { IncAlert } from 'components/inc-alert/inc-alert.component';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export type DeleteEmailTemplateDrawerDataType = {
  templateId: string;
};

export type DeleteEmailTemplateDrawerProps =
  IncDrawerType<DeleteEmailTemplateDrawerDataType>;

const DeleteEmailTemplateDrawer = ({
  isOpen,
  close,
  data: drawerData,
}: DeleteEmailTemplateDrawerProps) => {
  const { t } = useTranslation();

  const shouldFetchRelatedItems = !!drawerData?.templateId;
  const { currentData, isFetching } = useListEmailTemplateRelatedItemsQuery(
    {
      id: drawerData?.templateId,
    },
    { skip: !shouldFetchRelatedItems },
  );

  const [deleteEmailTemplateMutation] = useDeleteEmailTemplateMutation();

  const handleDeleteEmailTemplate = async () => {
    await deleteEmailTemplateMutation({ id: drawerData.templateId });
    close();
  };

  const canDelete = useMemo(() => currentData?.length === 0, [currentData]);

  return (
    <ConfirmationDrawer
      open={isOpen}
      close={close}
      color="error"
      onValid={handleDeleteEmailTemplate}
      submitButtonLabel={t(
        'drawer.deleteEmailTemplateDrawer.submitButtonLabel',
      )}
      deleteBlocker={{
        disable: !currentData || !canDelete,
        loading: isFetching,
      }}
    >
      {currentData && canDelete && (
        <IncAlert
          severity="error"
          description={
            <Box>
              {t('drawer.deleteEmailTemplateDrawer.deleteConfirmationAlert')}
            </Box>
          }
        />
      )}
      {currentData && !canDelete && (
        <IncAlert
          severity="error"
          description={
            <Box>
              <Box>
                {t('drawer.deleteEmailTemplateDrawer.deleteRestrictionAlert1')}
                <List data-testid="EmailTemplateRelatedItem.List">
                  {currentData?.map((item, index) => (
                    <ListItem
                      sx={{ py: 0 }}
                      key={index}
                      data-testid="EmailTemplateRelatedItem.List.Item"
                    >
                      <ListItemIcon>
                        <Circle sx={{ mr: -1, fontSize: 8 }} />
                      </ListItemIcon>
                      <MuiLink
                        component={Link}
                        to={`/communications/offers/${item.offerId}`}
                        target="_blank"
                      >
                        {item.title}
                      </MuiLink>
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box mt={2}>
                {t('drawer.deleteEmailTemplateDrawer.deleteRestrictionAlert2')}
              </Box>
            </Box>
          }
        />
      )}
    </ConfirmationDrawer>
  );
};

export default DeleteEmailTemplateDrawer;
